import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Table2, Banknote, Users, LogOut, Coins, Scale, History, BookOpen, Sparkles, Book, Landmark, UserCog, FolderClock, AlertTriangle, GitCompare } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";

const navItems = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Resumen" },
  { to: "/cashflow", icon: Table2, label: "Cashflow" },
  { to: "/bancos", icon: Banknote, label: "Bancos" },
  { to: "/gastos-fijos", icon: Coins, label: "Gastos fijos" },
  { to: "/gastos-fijos-estructura", icon: Landmark, label: "Estructura + IA", ownerOnly: true },
  { to: "/nominas", icon: UserCog, label: "Nóminas", ownerOnly: true },
  { to: "/impuestos", icon: Scale, label: "Impuestos / Otros" },
  { to: "/snapshots", icon: History, label: "Evolución" },
  { to: "/duplicados", icon: AlertTriangle, label: "Duplicados", ownerOnly: true, badge: "dup" },
  { to: "/comparador", icon: GitCompare, label: "Comparador Excel", ownerOnly: true },
  { to: "/archivos", icon: FolderClock, label: "Archivos semanales" },
  { to: "/manual", icon: BookOpen, label: "Manual" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const isOwner = (user?.email || "").toLowerCase() === "eduardo@grabarte360.com";
  const autoSyncFired = useRef(false);
  const [dupCount, setDupCount] = useState(null);

  // Duplicados count (badge) - solo owner, cada 5min
  useEffect(() => {
    if (!isOwner) return;
    const load = async () => {
      try {
        const r = await api.get(`/duplicados?year=${new Date().getFullYear()}`);
        setDupCount(r.data?.severity_counts?.alta || 0);
      } catch (_e) { /* silencio */ }
    };
    load();
    const iv = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(iv);
  }, [isOwner]);

  // Auto-sync con la API de Proyectos al entrar (solo owner, con throttle 5 min).
  // NOTA: sólo sincroniza FACTURAS/GASTOS de proyectos. Los gastos fijos
  // (nóminas, IVA, IRPF, préstamos, estructura) se gestionan MANUALMENTE
  // en Cashflow — se dejará de traer automáticamente hasta nueva orden.
  useEffect(() => {
    if (!isOwner || autoSyncFired.current) return;
    autoSyncFired.current = true;
    const KEY = "grbc_last_auto_sync_ts";
    const now = Date.now();
    const last = parseInt(localStorage.getItem(KEY) || "0", 10);
    const FIVE_MIN = 5 * 60 * 1000;
    if (now - last < FIVE_MIN) return; // throttle
    const year = new Date().getFullYear();
    (async () => {
      try {
        const t = toast.loading("Sincronizando con Proyectos…", { duration: 20000 });
        const r1 = await api.post(`/integration/proyectos/sync?year=${year}`).catch(e => ({ error: e }));
        toast.dismiss(t);
        const ing = r1?.data?.inserted ?? null;
        if (ing != null) {
          toast.success(`Proyectos sincronizado: ${ing} mov. (${year})`, { duration: 4000 });
          localStorage.setItem(KEY, String(now));
          window.dispatchEvent(new CustomEvent("proyectos:synced", { detail: { ing } }));
        }
      } catch (e) {
        toast.dismiss();
        console.warn("Auto-sync Proyectos falló:", e?.response?.data?.detail || e.message);
      }
    })();
  }, [isOwner]);

  return (
    <div className="min-h-screen flex bg-[var(--bg)] text-[var(--ink)]">
      <aside className="w-60 shrink-0 border-r border-[var(--border)] bg-white flex flex-col" data-testid="sidebar">
        <div className="px-6 py-6 border-b border-[var(--border)]">
          <Link to="/dashboard" className="block" data-testid="brand-link">
            <div className="font-display text-xl font-black leading-none tracking-tight">Grabarte<span className="text-[var(--ink-muted)]">·</span>Cashflow</div>
            <div className="font-mono-num text-[10px] uppercase tracking-widest text-[var(--ink-muted)] mt-1">Panel financiero</div>
          </Link>
        </div>
        <nav className="flex-1 py-4">
          {navItems.filter(it => !it.ownerOnly || isOwner).map(({ to, icon: Icon, label, badge }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${to.slice(1)}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-2.5 text-sm transition-colors ${
                  isActive ? "bg-black text-white" : "text-[var(--ink)] hover:bg-[var(--hover)]"
                }`
              }
            >
              <Icon size={16} strokeWidth={1.8} />
              <span className="flex-1">{label}</span>
              {badge === "dup" && dupCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-rose-600 text-white text-[10px] font-bold font-mono-num" data-testid="nav-dup-badge">{dupCount}</span>
              )}
            </NavLink>
          ))}
          {isOwner && (
            <>
              <NavLink
                to="/kpis"
                data-testid="nav-kpis"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-6 py-2.5 text-sm transition-colors ${
                    isActive ? "bg-black text-white" : "text-[var(--ink)] hover:bg-[var(--hover)]"
                  }`
                }
              >
                <Sparkles size={16} strokeWidth={1.8} />
                <span>KPIs</span>
              </NavLink>
              <NavLink
                to="/bitacora"
                data-testid="nav-bitacora"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-6 py-2.5 text-sm transition-colors ${
                    isActive ? "bg-black text-white" : "text-[var(--ink)] hover:bg-[var(--hover)]"
                  }`
                }
              >
                <Book size={16} strokeWidth={1.8} />
                <span>Bitácora</span>
              </NavLink>
            </>
          )}
          {user?.role === "admin" && (
            <NavLink
              to="/usuarios"
              data-testid="nav-usuarios"
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-2.5 text-sm transition-colors ${
                  isActive ? "bg-black text-white" : "text-[var(--ink)] hover:bg-[var(--hover)]"
                }`
              }
            >
              <Users size={16} strokeWidth={1.8} />
              <span>Usuarios</span>
            </NavLink>
          )}
        </nav>
        <div className="border-t border-[var(--border)] p-4">
          <div className="flex items-center gap-3 mb-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-8 w-8 object-cover" />
            ) : (
              <div className="h-8 w-8 bg-[var(--hover)] flex items-center justify-center text-xs font-bold">
                {user?.name?.[0] || "U"}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-xs font-medium truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-[10px] text-[var(--ink-muted)] truncate font-mono-num">{user?.email}</div>
              <div className="text-[10px] uppercase tracking-wider mt-0.5 inline-block px-1.5 py-px bg-[var(--ink)] text-white">
                {user?.role}
              </div>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="w-full inline-flex items-center justify-center gap-2 border border-[var(--border)] text-xs py-2 hover:bg-[var(--hover)] transition-colors"
          >
            <LogOut size={14} /> Cerrar sesión
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto flex flex-col" data-testid="main-content">
        <div className="flex-1 overflow-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
